import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../styles.css';
import Footer from '../components/Footer';
import PageHero from '../components/PageHero';
import { setPageMeta } from '../utils/pageMeta';
import heroImg from '../assets/programs/programs-hero.jpg';

// CalOPPA (Cal. Bus. & Prof. Code § 22575(b)(5)) requires the policy to carry
// an effective date, and Google's OAuth branding review looks for one too.
// Bump this whenever the copy below changes in substance.
const LAST_UPDATED = 'September 23, 2026';

export default function Privacy() {
    useEffect(() => {
        setPageMeta(
            'Privacy Policy - CHOPSTICKSTORY',
            'How CHOPSTICKSTORY collects, uses, shares, and retains personal information, and the privacy rights available to California residents.',
        );
        window.scrollTo({ top: 0 });
    }, []);

    return (
        <div className="legal-page">
            <PageHero image={heroImg} title="Privacy Policy" />

            <main className="legal-main">
                <article className="legal-doc">
                    <p className="legal-doc__updated">
                        Last updated: {LAST_UPDATED}
                    </p>

                    <p className="legal-doc__lead">
                        CHOPSTICKSTORY is a California nonprofit public benefit
                        corporation and a 501(c)(3) tax-exempt organization
                        (EIN 99-0860654). This Privacy Policy explains what
                        personal information we collect through
                        chopstickstory.org, why we collect it, who we share it
                        with, and the choices you have.
                    </p>

                    <section aria-labelledby="privacy-scope">
                        <h2 id="privacy-scope">1. Scope</h2>
                        <p>
                            This policy covers the CHOPSTICKSTORY website and
                            the accounts, volunteer applications, event
                            registrations, and service records available
                            through it. It does not cover information you give
                            us in person at a program, or the practices of
                            third-party sites we link to.
                        </p>
                    </section>

                    <section aria-labelledby="privacy-collect">
                        <h2 id="privacy-collect">
                            2. Information we collect
                        </h2>

                        <h3>Information you give us</h3>
                        <ul>
                            <li>
                                <strong>Account.</strong> Your email address
                                and password (stored only as a salted hash).
                                Optionally, your first and last name, profile
                                photo, short biography, gender, date of birth,
                                and phone number.
                            </li>
                            <li>
                                <strong>Volunteer application.</strong> Your
                                name, gender, date of birth, email address,
                                phone number, school, grade, preferred
                                departments, whether you consent to being
                                photographed at our programs, and your typed
                                signature and signature date.
                            </li>
                            <li>
                                <strong>Event registration.</strong> The
                                attendee name you supply, the events you
                                register for, and your attendance and
                                check-in/check-out times.
                            </li>
                            <li>
                                <strong>Service and work records.</strong> For
                                volunteers, interns, and instructors: the
                                program, date, description (for instructors,
                                the classes you taught), and number of hours
                                you log, along with the review status of each
                                entry.
                            </li>
                            <li>
                                <strong>Correspondence.</strong> Anything you
                                send to info@chopstickstory.org.
                            </li>
                        </ul>

                        <h3>Information from Google Sign-In</h3>
                        <p>
                            When you sign in with Google, we receive 
                            an identity token containing your Google 
                            account identifier, email address, and, 
                            where available, your name and profile 
                            picture URL. We also process security 
                            information in the token to verify that 
                            the sign-in is valid. We use your Google 
                            account identifier to recognize your account 
                            on future sign-ins, even if your email address 
                            changes.
                        </p>
                        <p>
                            We do not request, receive, or store your Google
                            password, and we have no access to your Gmail,
                            Google Drive, Contacts, Calendar, or any other
                            Google service.
                        </p>

                        <h3>Information collected automatically</h3>
                        <ul>
                            <li>
                                <strong>Server logs.</strong> Our servers
                                record standard request information such as IP
                                address, browser user agent, requested URL, and
                                timestamp, for security and troubleshooting.
                            </li>
                            <li>
                                <strong>Browser storage.</strong> After you
                                sign in we store authentication tokens in your
                                browser&rsquo;s local storage so you stay signed
                                in. These are not advertising identifiers and
                                are not shared with anyone.
                            </li>
                        </ul>
                    </section>

                    <section aria-labelledby="privacy-use">
                        <h2 id="privacy-use">
                            3. How we use your information
                        </h2>
                        <ul>
                            <li>To create and maintain your account.</li>
                            <li>
                                To verify your email address and, where
                                applicable, your phone number.
                            </li>
                            <li>
                                To review volunteer applications and to record
                                and certify volunteer service hours.
                            </li>
                            <li>
                                To manage event registration and attendance.
                            </li>
                            <li>
                                To send transactional messages about your
                                account, your application, or an event you
                                registered for.
                            </li>
                            <li>
                                To keep the site secure, prevent abuse, and
                                comply with the law.
                            </li>
                        </ul>
                        <p>
                            We do not use your information to build advertising
                            profiles, and we do not run behavioral advertising
                            on this site.
                        </p>
                    </section>

                    <section aria-labelledby="privacy-google">
                        <h2 id="privacy-google">
                            4. Google user data and Limited Use
                        </h2>
                        <p>
                            CHOPSTICKSTORY&rsquo;s use and transfer of
                            information received from Google APIs comply with the{' '}
                            <a
                                href="https://developers.google.com/terms/api-services-user-data-policy"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Google API Services User Data Policy
                            </a>
                            , including the Limited Use requirements where applicable. 
                            We use the Google account information described in Section 2 
                            to authenticate you, maintain your account and profile, and 
                            provide the services described in Section 3. We do not sell 
                            this information or use it for advertising. We share it only 
                            as necessary to provide those services, subject to applicable 
                            consent requirements, to comply with applicable law, or as part 
                            of a merger, acquisition, or transfer of assets with your 
                            explicit prior consent.
                        </p>
                        <p>
                            You can review or remove CHOPSTICKSTORY&rsquo;s
                            Google Sign-In connection in your Google account settings{' '}
                            <a
                                href="https://myaccount.google.com/permissions"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                myaccount.google.com/permissions
                            </a>
                            . After removing the connection, you will need to grant consent 
                            again to sign in with Google. Removing the connection does not 
                            delete your CHOPSTICKSTORY account or information we have already 
                            received. See Section 7 for retention and deletion.
                        </p>
                    </section>

                    <section aria-labelledby="privacy-share">
                        <h2 id="privacy-share">
                            5. How we share your information
                        </h2>
                        <p>
                            <strong>
                                We do not sell or rent your personal
                                information, and we do not share it for
                                cross-context behavioral advertising.
                            </strong>{' '}
                            We share it only in these situations:
                        </p>
                        <ul>
                            <li>
                                <strong>Service providers.</strong> Vendors who
                                process data on our behalf and under contract:
                                cloud hosting and database providers, object
                                storage for uploaded images (Cloudflare R2), our transactional
                                email provider (Microsoft Azure Communication
                                Services), and Google for sign-in.
                            </li>
                            <li>
                                <strong>Donations.</strong> Donations are
                                processed by PayPal on PayPal&rsquo;s own site
                                under PayPal&rsquo;s privacy policy. We receive
                                only the donor information PayPal reports to us.
                            </li>
                            <li>
                                <strong>Within our organization.</strong> Staff
                                and board members who need the information to
                                review applications, verify service hours, or
                                run a program.
                            </li>
                            <li>
                                <strong>Legal reasons.</strong> When required by
                                law, subpoena, or court order, or when necessary
                                to protect the rights, property, or safety of
                                CHOPSTICKSTORY, our participants, or the public.
                            </li>
                            <li>
                                <strong>Organizational changes.</strong> In
                                connection with a merger, dissolution, or
                                transfer of assets, subject to this policy.
                            </li>
                        </ul>
                    </section>

                    <section aria-labelledby="privacy-cookies">
                        <h2 id="privacy-cookies">
                            6. Cookies, local storage, and Do Not Track
                        </h2>
                        <p>
                            We use browser local storage for authentication
                            tokens and session state. We do not use advertising
                            or third-party analytics cookies on this site.
                        </p>
                        <p>
                            Some pages load fonts and icons from Google Fonts
                            and Font Awesome, and those providers may see your
                            IP address as part of serving the file.
                        </p>
                        <p>
                            <strong>Do Not Track.</strong> Because we do not
                            track visitors across third-party websites over
                            time, we do not respond differently to Do Not Track
                            browser signals. We also do not permit third parties
                            to collect personally identifiable information about
                            your online activities across other sites when you
                            visit ours.
                        </p>
                    </section>

                    <section aria-labelledby="privacy-retention">
                        <h2 id="privacy-retention">
                            7. Retention and account deletion
                        </h2>
                        <p>
                            We keep your account information for as long as
                            your account is active. When you delete your
                            account, we immediately deactivate it and mark it
                            for removal; the record is permanently purged 30
                            days later. Deleting an account created with Google
                            Sign-In requires re-authenticating with Google
                            first, so that no one else can delete it for you.
                        </p>
                        <p>
                            We may retain certified volunteer service records
                            and donation records for longer where we are
                            required to do so by tax, grant, or school
                            service-hour reporting obligations, or to resolve a
                            dispute. Server logs are retained for a limited
                            period for security purposes.
                        </p>
                    </section>

                    <section aria-labelledby="privacy-security">
                        <h2 id="privacy-security">8. Security</h2>
                        <p>
                            We serve the site over HTTPS, store passwords only
                            as salted hashes, verify Google identity tokens
                            server-side, and restrict administrative access to
                            authorized personnel. No method of transmission or
                            storage is perfectly secure, so we cannot guarantee
                            absolute security.
                        </p>
                        <p>
                            If we discover a security incident involving personal 
                            information, we will assess the incident and provide 
                            notices to affected individuals and relevant authorities 
                            as required by applicable law.
                        </p>
                    </section>

                    <section aria-labelledby="privacy-minors">
                        <h2 id="privacy-minors">
                            9. Children and minors
                        </h2>
                        <p>
                            Our online accounts are intended for people 13 years
                            of age or older. We do not knowingly collect
                            personal information online from children under 13.
                            If you believe a child under 13 has created an
                            account, contact us at info@chopstickstory.org and
                            we will delete it.
                        </p>
                        <p>
                            Many of our volunteers are high school students. If
                            you are under 18, please apply only with the
                            knowledge and permission of a parent or guardian. A
                            parent or guardian may contact us to review, correct,
                            or delete their minor child&rsquo;s information.
                        </p>
                        <p>
                            <strong>
                                California minors&rsquo; right to removal.
                            </strong>{' '}
                            Under California Business and Professions Code
                            § 22581, a registered user who is under 18 and a
                            California resident may request removal of content
                            or information they posted on this site. Email
                            info@chopstickstory.org with the details and we will
                            remove it or make it invisible to other users.
                            Removal may not be complete or comprehensive — for
                            example, where the law requires us to keep the
                            information, or where someone else reposted it.
                        </p>
                    </section>

                    <section aria-labelledby="privacy-rights">
                        <h2 id="privacy-rights">
                            10. Your California privacy rights
                        </h2>
                        <p>
                            The California Consumer Privacy Act, as amended by
                            the California Privacy Rights Act (Cal. Civ. Code
                            § 1798.100 <em>et seq.</em>), generally applies to
                            entities organized or operated for profit.
                            CHOPSTICKSTORY is a nonprofit, so it is generally
                            not a &ldquo;business&rdquo; under that statute.
                            Even so, we extend the following rights to everyone
                            who uses this site, as a matter of policy:
                        </p>
                        <ul>
                            <li>
                                <strong>Know.</strong> Request the categories
                                and specific pieces of personal information we
                                hold about you.
                            </li>
                            <li>
                                <strong>Access and portability.</strong> Request
                                a copy of that information.
                            </li>
                            <li>
                                <strong>Correct.</strong> Ask us to fix
                                inaccurate information. Most profile fields can
                                be edited directly on your profile page.
                            </li>
                            <li>
                                <strong>Delete.</strong> Delete your account
                                from your profile page, or ask us to delete it.
                            </li>
                            <li>
                                <strong>Non-discrimination.</strong> We will not
                                treat you differently for exercising any of
                                these rights.
                            </li>
                        </ul>
                        <p>
                            To make a request, email info@chopstickstory.org
                            from the address on your account, or include enough
                            detail for us to verify who you are. We will respond
                            within 45 days. An authorized agent may submit a
                            request with your written permission.
                        </p>
                        <p>
                            <strong>Shine the Light.</strong> California Civil
                            Code § 1798.83 lets California residents ask
                            businesses about personal information disclosed to
                            third parties for those third parties&rsquo; direct
                            marketing. We do not disclose personal information
                            to third parties for their direct marketing
                            purposes.
                        </p>
                    </section>

                    <section aria-labelledby="privacy-changes">
                        <h2 id="privacy-changes">
                            11. Changes to this policy
                        </h2>
                        <p>
                            We will update the &ldquo;Last updated&rdquo; date when this policy 
                            changes. We will provide additional notice of material 
                            changes and obtain your consent where required by applicable 
                            law or the policies governing a third-party service we use.
                        </p>
                    </section>

                    <section aria-labelledby="privacy-contact">
                        <h2 id="privacy-contact">12. Contact us</h2>
                        <p>
                            Questions, requests, or complaints about this policy:
                        </p>
                        <address>
                            CHOPSTICKSTORY
                            <br />
                            20570 Shady Oak Ln, Cupertino, CA
                            <br />
                            <a href="mailto:info@chopstickstory.org">
                                info@chopstickstory.org
                            </a>
                            <br />
                            (607) 973-0750
                        </address>
                        <p>
                            See also our <Link to="/terms">Terms of Service</Link>.
                        </p>
                    </section>
                </article>
            </main>

            <Footer />
        </div>
    );
}
